import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "../FIREBASE/useAuth";
import { fetchTrainingData, createTrainingEvents, createTrainingEvent, deleteTrainingEvent, updateEventCategory, CreateEventPayload, setIntervalsCreds, hasIntervalsCreds } from "../FIREBASE/intervals";
import { loadCredentials } from "../FIREBASE/userSettings";
import { generateTrainingPlan, clampPlanStart } from "../COMPONENTS/planGenerator";
import { generateCapacityPlan } from "../COMPONENTS/capacityPlan";
import { isEnduranceSport } from "../COMPONENTS/plannerProfile";
import { customBankSessions, loadFavoriteIds } from "../COMPONENTS/intervalBank";
import { loadPlanActionOverrides } from "../COMPONENTS/planActionOverrides";
import { initCloudSync } from "../COMPONENTS/cloudSync";
import { publishPlan, PublishApi } from "../COMPONENTS/publishPlan";
import { savePlanWeekMeta } from "../COMPONENTS/planWeekMeta";
import { loadProfile, saveProfile, pickSessionName, recentVolume, historicalHardFraction } from "../COMPONENTS/plannerProfile";
import { TrainingData, RaceTarget, PlannedWeek, PlannedSession, PlannerProfile, SportType } from "../types/training";
import { TrainingPlanView } from "../VIEW/TrainingPlanView";
import { OnboardingWizard } from "../VIEW/OnboardingWizard";
import { TrainingStatusScreen } from "../COMPONENTS/TrainingStatusScreen";
import routeUrls from "../COMPONENTS/navbar/navLink";

export interface RacePlan {
  race: RaceTarget;
  weeks: PlannedWeek[];
  sessions: PlannedSession[];
  windowStart: string; // "YYYY-MM-DD" — this plan owns [windowStart, race]; chained plans don't reach back to today
  flaggedDates: Map<string, string>;
  expectedRaceTSB: number;
  volumeNote?: string;
  plannedHardPct: number;
  historicalHardPct?: number;
  publishing: boolean;
  progress: { done: number; total: number; phase: "create" | "clean" } | null;
  published: boolean;
}

function applyProfileNames(sessions: PlannedSession[], profile: PlannerProfile): PlannedSession[] {
  return sessions.map(s => {
    const slot: "long" | "quality" | "easy" =
      /long/i.test(s.name) ? "long" :
      /threshold|interval/i.test(s.name) ? "quality" : "easy";
    return { ...s, name: pickSessionName(profile, s.sport, slot, s.name) };
  });
}

function TrainingPlanPage() {
  const { user, loading: authLoading } = useAuth();
  const history = useHistory();
  const [data, setData] = useState<TrainingData | null>(null);
  const [profile, setProfile] = useState<PlannerProfile | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [plans, setPlans] = useState<RacePlan[]>([]);
  const [error, setError] = useState("");
  const [hydrated, setHydrated] = useState(false); // account settings pulled into localStorage

  useEffect(() => {
    if (!authLoading && !user) {
      history.replace(`${routeUrls.LOGIN}?from=${routeUrls.TRAINING_PLAN}`);
    }
  }, [user, authLoading, history]);

  useEffect(() => {
    if (authLoading || !user) return;
    let cancelled = false;

    async function init() {
      if (!hasIntervalsCreds()) {
        const creds = await loadCredentials(user!.uid);
        if (cancelled) return;
        if (!creds) { history.push(routeUrls.CONNECT); return; }
        setIntervalsCreds(creds.apiKey, creds.athleteId);
      }

      // Pull account-synced settings (wizard profile, favorites, etc.) into
      // localStorage BEFORE reading them, so they follow the user across devices.
      await initCloudSync(user!.uid);
      if (cancelled) return;
      setHydrated(true);

      try {
        const { wellness: wellnessArr, activities, events } = await fetchTrainingData();
        if (cancelled) return;
        const wellnessHistory = (wellnessArr as any[]).map((w: any) => ({
          id: w.id, ctl: w.ctl ?? null, atl: w.atl ?? null,
          hrv: w.hrv ?? null, restingHR: w.restingHR ?? null,
        }));
        const latest = wellnessHistory[wellnessHistory.length - 1];
        setData({
          wellness: { ctl: latest?.ctl ?? null, atl: latest?.atl ?? null, restingHR: latest?.restingHR ?? null, hrv: latest?.hrv ?? null },
          wellnessHistory,
          activities: activities as any[],
          events: events as any[],
        });
        const saved = loadProfile();
        if (saved) { setProfile(saved); } else { setShowWizard(true); }
      } catch {
        if (!cancelled) setError("Could not load training data.");
      }
    }

    init();
    return () => { cancelled = true; };
  }, [user, authLoading, history]);

  const handleWizardComplete = useCallback((p: PlannerProfile) => {
    saveProfile(p);
    setProfile(p);
    setShowWizard(false);
  }, []);

  const handleGenerate = useCallback((races: RaceTarget[]) => {
    const ctl = data?.wellness.ctl ?? 50;
    const realToday = new Date();
    const calendarEvents = data?.events ?? [];
    const activities = data?.activities ?? [];

    // IDs of sessions we ourselves published — so the planner recognises them as
    // its own plan rather than treating them as conflicts to move around.
    let ownEventIds: number[] = [];
    try { ownEventIds = Object.values(JSON.parse(localStorage.getItem("published-plan-ids") || "{}") as Record<string, number[]>).flat(); } catch {}

    // Athlete's interval-bank preferences: custom sessions become extra
    // candidates; favorites are preferred by the picker within their tier.
    const customSessions = customBankSessions();
    const favoriteSessionIds = loadFavoriteIds();

    // Sort races chronologically so earlier races are planned first
    const sortedAll = [...races].sort((a, b) => a.date.localeCompare(b.date));

    // Non-endurance goals (alpine, and later the IronMan fystest) use the
    // capacity-periodization engine, not the CTL/TSS endurance ramp.
    const sorted = sortedAll.filter(r => isEnduranceSport(r.sport));
    const capacityGoals = sortedAll.filter(r => !isEnduranceSport(r.sport));

    // A-races (and the final race, whatever its priority) anchor full plans with
    // a taper + recovery + rebuild. B/C races are tune-ups folded into the
    // surrounding anchor's plan as hard efforts with a mini-taper.
    const anchors = sorted.filter((r, i) =>
      r.priority === "A" || r.priority == null || i === sorted.length - 1
    );
    const tuneUps = sorted.filter(r => !anchors.includes(r));

    const generated: RacePlan[] = [];

    // Capacity (alpine / fystest) goals — each independent, no taper-chaining.
    for (const goal of capacityGoals) {
      const { weeks, sessions: rawSessions, flaggedDates, expectedRaceTSB, volumeNote, plannedHardPct } = generateCapacityPlan({
        goal,
        today: realToday,
        currentCTL: ctl,
        preferredDays: profile?.preferredDays,
        blockedRanges: profile?.blockedRanges,
        sickSince: profile?.sickSince,
      });
      const sessions = profile ? applyProfileNames(rawSessions, profile) : rawSessions;
      generated.push({ race: goal, weeks, sessions, windowStart: realToday.toISOString().slice(0, 10), flaggedDates, expectedRaceTSB, volumeNote, plannedHardPct, publishing: false, progress: null, published: false });
    }
    const accumulatedSessions: { start_date_local: string }[] = [...calendarEvents];
    let startFrom = realToday;
    let prevRaceDate: Date | null = null;

    for (const race of anchors) {
      // Don't let the previous race's recovery overshoot this race — guarantee a
      // runway so a closely-following race still gets a plan (not "för nära").
      startFrom = clampPlanStart(startFrom, new Date(race.date + "T12:00:00"), prevRaceDate, realToday);
      const windowStart = startFrom.toISOString().slice(0, 10); // this plan owns [windowStart, race]

      const tsb = data?.wellness.ctl != null && data?.wellness.atl != null
        ? data.wellness.ctl - data.wellness.atl : null;

      // Training history (up to a year) for this race's sport.
      const { weeklyHours, longestHours, peakWeeklyHours } = recentVolume(activities, race.sport);
      const histHard = historicalHardFraction(activities);

      // Tune-up races that fall within this anchor's plan window.
      const winStart = startFrom.toISOString().slice(0, 10);
      const planTuneUps = tuneUps
        .filter(t => t.date > winStart && t.date < race.date)
        .map(t => ({ date: t.date, name: t.name }));

      const { weeks, sessions: rawSessions, flaggedDates, expectedRaceTSB, volumeNote, plannedHardPct, historicalHardPct } = generateTrainingPlan({
        race,
        currentCTL: ctl,
        currentTSB: tsb,
        today: realToday,
        startFrom,
        existingEvents: accumulatedSessions as any,
        preferredDays: profile?.preferredDays ?? { long: [5], hard: [1], easy: [0, 3], gym: [] },
        maxWeeklyHours: profile?.maxWeeklyHours ?? 8,
        planningMode: profile?.planningMode ?? "tss",
        sportsEnjoyed: profile?.sportsEnjoyed,
        commute: profile?.commute,
        recentWeeklyHours: weeklyHours,
        recentLongestHours: longestHours,
        peakAchievedHours: peakWeeklyHours,
        historicalHardFraction: histHard ?? undefined,
        tuneUpRaces: planTuneUps,
        allowDoubles: profile?.allowDoubles ?? false,
        doubleThreshold: profile?.doubleThreshold ?? false,
        primarySport: profile?.primarySport,
        snowStartMonth: profile?.snowStartMonth,
        snowEndMonth: profile?.snowEndMonth,
        fixedSessions: profile?.fixedSessions,
        blockedRanges: profile?.blockedRanges,
        sickSince: profile?.sickSince,
        ownEventIds,
        customSessions,
        favoriteSessionIds,
      });
      const sessions = profile ? applyProfileNames(rawSessions, profile) : rawSessions;
      generated.push({ race, weeks, sessions, windowStart, flaggedDates, expectedRaceTSB, volumeNote, plannedHardPct, historicalHardPct, publishing: false, progress: null, published: false });

      // Next anchor plan starts 2 weeks after this race (recovery period); the
      // clamp above re-bounds it if the next race is too close to fit that.
      const raceDate = new Date(race.date + "T12:00:00");
      prevRaceDate = raceDate;
      startFrom = new Date(raceDate.getTime() + 14 * 86400000);

      for (const s of sessions) {
        accumulatedSessions.push({ start_date_local: s.date + "T10:00:00" });
      }
    }

    setPlans(generated);
    // Persist a per-week summary (phase / planned hours / hard%) so the Training
    // dashboard calendar can show the same metrics without re-running the planner.
    savePlanWeekMeta(generated);
  }, [data, profile]);

  const handlePublish = useCallback(async (index: number) => {
    const plan = plans[index];
    if (!plan) return;

    const todayStr = new Date().toISOString().slice(0, 10);
    setPlans(prev => prev.map((p, i) =>
      i === index ? { ...p, publishing: true, progress: { done: 0, total: p.sessions.filter(s => !s.noPublish).length, phase: "create" } } : p
    ));

    // Dependency-injected intervals.icu adapter — see publishPlan() for the
    // create→refetch→delete reconciliation (unit-tested in publishPlan.test.ts).
    const api: PublishApi = {
      createEvents: (payloads, onProgress) => createTrainingEvents(payloads as CreateEventPayload[], onProgress),
      fetchEvents: async () => (await fetchTrainingData()).events as any,
      deleteEvent: deleteTrainingEvent,
    };

    try {
      await publishPlan(
        // windowStart bounds the publish window so a chained plan only touches its
        // own segment, never the whole timeline before it.
        api, plan.sessions, plan.race.endDate ?? plan.race.date, plan.windowStart ?? todayStr,
        progress => setPlans(prev => prev.map((p, i) => i === index ? { ...p, progress } : p)),
        loadPlanActionOverrides(),
      );
      // Re-fetch the real calendar so the view reflects the post-publish truth:
      // the plan's sessions are now ON intervals.icu, so they render as "kept"
      // (grey) instead of "new". The diff colouring matches each plan session to
      // its calendar event, so there are no duplicate chips.
      const refreshed = await fetchTrainingData();
      setData(prev => prev ? { ...prev, events: refreshed.events as any } : prev);
      setPlans(prev => prev.map((p, i) =>
        i === index ? { ...p, publishing: false, progress: null, published: true } : p
      ));
    } catch {
      setError("Could not publish plan. Try again.");
      setPlans(prev => prev.map((p, i) =>
        i === index ? { ...p, publishing: false, progress: null } : p
      ));
    }
  }, [plans]);

  const handleCreateRace = useCallback(async (name: string, date: string, sport: SportType) => {
    await createTrainingEvent({
      start_date_local: `${date}T12:00:00`,
      name,
      type: sport,
      description: "",
      category: "RACE_A",
    });
  }, []);

  // Persist a race-priority change to intervals.icu (its category) so it's saved
  // and survives reload. Optimistically update local state so the UI reflects it.
  const handleSetRacePriority = useCallback(async (eventId: number, priority: "A" | "B" | "C") => {
    const category = `RACE_${priority}`;
    setData(prev => prev ? { ...prev, events: prev.events.map(e => e.id === eventId ? { ...e, category } as any : e) } : prev);
    try {
      await updateEventCategory(eventId, category);
    } catch {
      setError("Kunde inte spara loppets prioritet till intervals.icu.");
    }
  }, []);

  if (authLoading || !user) return null;
  if (error)
    return (
      <TrainingStatusScreen
        title="Träningsplanering"
        backTo={routeUrls.TRAINING}
        backLabel="← Träning"
        message="Kunde inte ladda träningsdata."
        tone="error"
        onRetry={() => window.location.reload()}
      />
    );
  if (!hydrated)
    return (
      <TrainingStatusScreen
        title="Träningsplanering"
        backTo={routeUrls.TRAINING}
        backLabel="← Träning"
        message="Laddar…"
        tone="loading"
      />
    );

  if (showWizard && data) {
    return <OnboardingWizard activities={data.activities} events={data.events} initialProfile={profile} onComplete={handleWizardComplete} />;
  }

  return (
    <TrainingPlanView
      data={data}
      plans={plans}
      profile={profile}
      onGenerate={handleGenerate}
      onCreateRace={handleCreateRace}
      onPublish={handlePublish}
      onSetRacePriority={handleSetRacePriority}
      onEditProfile={() => setShowWizard(true)}
      onBack={() => history.push(routeUrls.TRAINING)}
    />
  );
}

export default TrainingPlanPage;
